import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { TrendingUp, TrendingDown, DollarSign, Percent } from 'lucide-react';

export function PnLSummary() {
  // Mock P&L data - in real app would come from API/blockchain
  const pnlData = {
    totalValue: 15420.50,
    totalCost: 13200.00,
    totalPnL: 2220.50,
    totalPnLPercent: 16.82,
    dayPnL: 234.75,
    dayPnLPercent: 1.54,
    weekPnL: -125.30,
    weekPnLPercent: -0.81,
    monthPnL: 1850.20,
    monthPnLPercent: 13.65,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercent = (percent: number) => {
    return `${percent >= 0 ? '+' : ''}${percent.toFixed(2)}%`;
  };

  const getPnLColor = (value: number) => {
    return value >= 0 ? 'text-green-600' : 'text-red-600';
  };

  const getPnLIcon = (value: number) => {
    return value >= 0 ? TrendingUp : TrendingDown;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Total Portfolio Value */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Portfolio Value</CardTitle>
          <DollarSign className="h-4 w-4 text-blue-600" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{formatCurrency(pnlData.totalValue)}</div>
          <div className="flex items-center gap-1 text-sm">
            {React.createElement(getPnLIcon(pnlData.totalPnL), {
              className: `h-3 w-3 ${getPnLColor(pnlData.totalPnL)}`
            })}
            <span className={getPnLColor(pnlData.totalPnL)}>
              {formatCurrency(Math.abs(pnlData.totalPnL))} ({formatPercent(pnlData.totalPnLPercent)})
            </span>
          </div>
        </CardContent>
      </Card>

      {/* 24h P&L */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">24h P&L</CardTitle>
          {React.createElement(getPnLIcon(pnlData.dayPnL), {
            className: `h-4 w-4 ${getPnLColor(pnlData.dayPnL)}`
          })}
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${getPnLColor(pnlData.dayPnL)}`}>
            {formatCurrency(Math.abs(pnlData.dayPnL))}
          </div>
          <p className={`text-sm ${getPnLColor(pnlData.dayPnL)}`}>
            {formatPercent(pnlData.dayPnLPercent)}
          </p>
        </CardContent>
      </Card>

      {/* 7d P&L */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">7d P&L</CardTitle>
          {React.createElement(getPnLIcon(pnlData.weekPnL), {
            className: `h-4 w-4 ${getPnLColor(pnlData.weekPnL)}`
          })}
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${getPnLColor(pnlData.weekPnL)}`}>
            {formatCurrency(Math.abs(pnlData.weekPnL))}
          </div>
          <p className={`text-sm ${getPnLColor(pnlData.weekPnL)}`}>
            {formatPercent(pnlData.weekPnLPercent)}
          </p>
        </CardContent>
      </Card>

      {/* 30d P&L */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">30d P&L</CardTitle>
          {React.createElement(getPnLIcon(pnlData.monthPnL), {
            className: `h-4 w-4 ${getPnLColor(pnlData.monthPnL)}`
          })}
        </CardHeader>
        <CardContent>
          <div className={`text-2xl font-bold ${getPnLColor(pnlData.monthPnL)}`}>
            {formatCurrency(Math.abs(pnlData.monthPnL))}
          </div>
          <p className={`text-sm ${getPnLColor(pnlData.monthPnL)}`}>
            {formatPercent(pnlData.monthPnLPercent)}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}